import React, { forwardRef } from "react";
import moment from "moment";
import cx from "classnames";
import styles from "./PrintReportPdf.module.css";
import { formatNumberWithCurrency } from "../../../utils/utils";
import { useLocalization } from "../../../context/LocalizeContextProvider";
import {
  findLabelPaymentMethod,
  findLabelPaymentType,
  typeDiscount,
  typeDiscountObj,
  paymentMethod,
} from "../../../utils/constants";

const PrintReportPdf = forwardRef(({ data }, ref) => {
  const localize = useLocalization();

  const dataHistoryPaymentTotal =
    data?.filter(
      (item, index, self) =>
        item.payment_id === null ||
        index === self.findIndex((t) => t.payment_id === item.payment_id)
    ) || [];

  const totalPrice = dataHistoryPaymentTotal.reduce((total, item) => {
    return total + (item.total_amount || 0);
  }, 0);
  const totalCorrectionPrice = dataHistoryPaymentTotal.reduce((total, item) => {
    return total + (item.total_amount_correction || 0);
  }, 0);

  const totalPriceFinal = totalPrice + totalCorrectionPrice;

  // Calculate totals by payment method
  const getTotalByPaymentMethod = () => {
    const totals = {};

    // Initialize totals for all payment methods
    paymentMethod.forEach((method) => {
      totals[method.value] = 0;
    });

    // Add "UNKNOWN" for unrecognized payment methods
    totals["UNKNOWN"] = 0;

    // Calculate totals — support split/multi-method payments
    dataHistoryPaymentTotal.forEach((item) => {
      const amount = item.total_amount || 0;
      const totalAmountWithCorrection =
        amount + (item.total_amount_correction || 0);

      // Parse payment_methods array if available
      let methodsArray = [];
      try {
        if (typeof item.payment_methods === "string") {
          methodsArray = JSON.parse(item.payment_methods);
        } else if (Array.isArray(item.payment_methods)) {
          methodsArray = item.payment_methods;
        }
      } catch (e) {}

      if (methodsArray && methodsArray.length > 0) {
        // Multi-method: distribute by actual amounts paid
        methodsArray.forEach((m) => {
          const mKey = m.method;
          if (totals.hasOwnProperty(mKey)) {
            totals[mKey] += Number(m.amount) || 0;
          } else {
            totals["UNKNOWN"] += Number(m.amount) || 0;
          }
        });
      } else {
        const method = item.payment_method;
        if (totals.hasOwnProperty(method)) {
          totals[method] += totalAmountWithCorrection;
        } else {
          totals["UNKNOWN"] += totalAmountWithCorrection;
        }
      }
    });

    return totals;
  };

  const paymentMethodTotals = getTotalByPaymentMethod();

  // Get payment method label or fallback
  const getPaymentMethodLabel = (method) => {
    if (method === "UNKNOWN") {
      return "-";
    }
    return findLabelPaymentMethod(method) || method;
  };

  return (
    <div ref={ref} className={cx(styles.tableContainer)}>
      <table className={styles.paymentTable}>
        <thead>
          <tr>
            <th>{localize.getText("noInvoice")}</th>
            <th>{localize.getText("namePatient")}</th>
            <th>{localize.getText("doctor")}</th>
            <th>{localize.getText("nameItem")}</th>
            <th>{localize.getText("priceItem")}</th>
            <th>{localize.getText("quantity")}</th>
            <th>{localize.getText("datePayment")}</th>
            <th>{localize.getText("paymentMethod")}</th>
            <th>{localize.getText("paymentType")}</th>
            <th>{localize.getText("totalPayment")}</th>
            <th>{localize.getText("discountType")}</th>
            <th>{localize.getText("discount")}</th>
          </tr>
        </thead>
        <tbody>
          {data.map((item, index) => (
            <tr key={index}>
              <td>{item.no_invoice}</td>
              <td>{item.user_name || "-"}</td>
              <td>{item.doctor_name || "-"}</td>
              <td>{item.name_item}</td>
              <td>{formatNumberWithCurrency(item?.amount_item || 0)}</td>
              <td>{item.quantity}</td>
              <td>
                {item.payment_date
                  ? moment(item.payment_date).format("DD MMM YYYY HH:mm")
                  : "-"}
              </td>
              <td>{findLabelPaymentMethod(item.payment_method)}</td>
              <td>{findLabelPaymentType(item.payment_type)}</td>
              <td>{formatNumberWithCurrency(item?.total_amount || 0)}</td>
              <td>
                {typeDiscount.find((type) => type.value === item.type_discount)
                  ?.label || "-"}
              </td>
              <td>
                {item.type_discount === typeDiscountObj.NUMBER
                  ? formatNumberWithCurrency(item.discount)
                  : item.discount || "-"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className={styles.paymentMethodTotals}>
        <h3 className={styles.totalSectionTitle}>Ringkasan Total Pembayaran</h3>
        <div className={styles.totalsGrid}>
          {Object.entries(paymentMethodTotals)
            .filter(([method, total]) => total > 0)
            .map(([method, total]) => (
              <div key={method} className={styles.totalRow}>
                <span className={styles.paymentMethodLabel}>
                  Total Pembayaran {getPaymentMethodLabel(method)}:
                </span>
                <span className={styles.paymentMethodAmount}>
                  {formatNumberWithCurrency(total)}
                </span>
              </div>
            ))}
        </div>
      </div>

      <div className={styles.wrapperTotalpayment}>
        <p>{localize.getText("totalPaymentBeforeCorrection")}</p>
        <p className={styles.paymentMethodAmount}>
          {formatNumberWithCurrency(totalPrice)}
        </p>
      </div>
      <div className={styles.wrapperTotalpayment}>
        <p>{localize.getText("totalCorrectionAmount")}</p>
        <p className={styles.correction}>
          {formatNumberWithCurrency(totalCorrectionPrice)}
        </p>
      </div>
      <div className={styles.wrapperTotalpayment}>
        <p>{localize.getText("totalPayment")}</p>
        <p className={styles.paymentMethodAmount}>
          {formatNumberWithCurrency(totalPriceFinal)}
        </p>
      </div>
    </div>
  );
});

export default PrintReportPdf;
